import classNames from 'classnames';
import { ReactNode } from 'react';
import { attempt, Button, Heading, ModalComponentProps } from 'yoso';
import { T } from '~/components/I18n';
import { Close } from '~/components/Icon/Close';
import { ModalContext, ModalContextValue } from '~/components/Modal/Modal.hooks';
import styles from './Modal.module.scss';

export type ModalProps = {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  confirmButton?: ReactNode | ((context: ModalContextValue) => ReactNode);
  confirmText?: ReactNode;
  dismissButton?: ReactNode | ((context: ModalContextValue) => ReactNode);
  dismissText?: ReactNode;
  closeButton?: ReactNode | ((context: ModalContextValue) => ReactNode);
  children?: ReactNode | ((context: ModalContextValue) => ReactNode);
} & Omit<JSX.IntrinsicElements['div'], 'title'> &
  ModalComponentProps;
export const Modal = ({
  icon,
  title,
  description,
  confirmText = <T name="citizen_confirm_ok" />,
  dismissText = <T name="citizen_button_title_next_time" />,
  className,
  children,
  onConfirm: confirm,
  onDismiss: dismiss,
  confirmButton = (
    <Button
      className={classNames(styles.button, styles.confirmButton)}
      color="primary"
      onClick={confirm}>
      {confirmText}
    </Button>
  ),
  dismissButton = (
    <Button
      className={classNames(styles.button, styles.dismissButton)}
      color="default"
      onClick={dismiss}>
      {dismissText}
    </Button>
  ),
  closeButton = (
    <Button className={styles.closeButton} variant="transparent" onClick={dismiss}>
      <Close />
    </Button>
  ),
  ...props
}: ModalProps) => {
  const value = {
    dismiss: (dismiss as ModalContextValue['dismiss'])!,
    confirm: (confirm as ModalContextValue['confirm'])!,
  };
  return (
    <ModalContext.Provider value={value}>
      <div className={classNames(styles.root, className)} {...props}>
        {attempt(closeButton, value)}
        {icon && <div className={styles.icon}>{icon}</div>}
        {title && (
          <Heading level={5} className={styles.heading}>
            {title}
          </Heading>
        )}
        {description && <p className={styles.description}>{description}</p>}
        {attempt(children, value)}
        <div className={styles.buttonGroup}>
          {attempt(dismissButton, value)}
          {attempt(confirmButton, value)}
        </div>
      </div>
    </ModalContext.Provider>
  );
};
